import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Clock, ArrowRight, X, MapPin, CalendarDays } from "lucide-react";
import { BASE_URL, batchesApi } from "../../../../api/api";
import { Link } from "react-router-dom";

const UpcomingBatches = () => {
    const [batches, setBatches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedBatch, setSelectedBatch] = useState(null);

    const sessionData = sessionStorage.getItem("studentSession");
    const token = sessionData ? JSON.parse(sessionData)?.token : null;
    const redirectUrl = token ? "/dashboard" : "/login";

    useEffect(() => {
        const fetchBatches = async () => {
            try {
                const response = await fetch(`${BASE_URL}${batchesApi}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const data = await response.json();
                setBatches(data || []);
            } catch (error) {
                console.error("Error fetching batches:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchBatches();
    }, [token]);

    const openPopup = (batch) => setSelectedBatch(batch);
    const closePopup = () => setSelectedBatch(null);

    const safeReplace = (html) => {
        // Render the rich HTML description as-is, but remove only dangerous bits
        // (script/style blocks + event-handler attributes + javascript: URLs).
        if (typeof html !== "string") return "";
        let s = html;
        // strip <script>...</script> and <style>...</style> blocks
        s = s.replace(/<\/?(script|style)[^>]*>/gi, "");
        // remove event-handler attributes (onclick, onerror, etc.) and javascript: URLs
        s = s.replace(/\s+on[a-z]+\s*"/gi, "");
        s = s.replace(/\s*(href|src|action)\s*=\s*"[^"]*javascript:/gi, "");
        return s;
    };

    const formatTicketDate = (dateString) => {
        const date = new Date(dateString);
        return {
            day: date.toLocaleDateString("en-GB", { day: "2-digit" }),
            month: date
                .toLocaleDateString("en-GB", { month: "short" })
                .toUpperCase(),
            year: date.toLocaleDateString("en-GB", { year: "numeric" }),
        };
    };

    const activeBatches = batches.filter(
        (batch) => batch.status === 1 || batch.status === true,
    );

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: { opacity: 1, transition: { staggerChildren: 0.05 } },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    return (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50 w-full">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12 border-b border-gray-200 pb-6">
                    <div>
                        <h2 className="text-3xl md:text-4xl font-black text-neutral-900">
                            {/* Upcoming Batches& qna sessions */}
                            Upcoming
                            <span className="text-yellow-400">
                                {" "}
                                Batches & Q&A{" "}
                            </span>
                            Sessions
                        </h2>
                    </div>
                    <p className="text-gray-500 text-sm md:text-base max-w-md font-medium leading-relaxed">
                        Secure your position in our next intensive preparation
                        cohort. Review schedules and reserve your seat below.
                    </p>
                </div>

                {/* Loading State */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {[1, 2, 3, 4].map((i) => (
                            <div
                                key={i}
                                className="bg-white rounded-xl border border-gray-200 shadow-sm animate-pulse h-48"
                            ></div>
                        ))}
                    </div>
                ) : activeBatches.length > 0 ? (
                    /* Compact Professional Grid */
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                        variants={containerVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true, margin: "-50px" }}
                    >
                        {activeBatches.map((batch, index) => {
                            const dateInfo = formatTicketDate(batch.start_date);
                            return (
                                <motion.div
                                    key={index}
                                    variants={itemVariants}
                                    className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-yellow-400 transition-all duration-300 flex flex-col group overflow-hidden"
                                >
                                    {/* Abstract Ticket Stub (Yellow Header) */}
                                    <div className="bg-yellow-400 px-5 py-4 flex justify-between items-center">
                                        <div className="flex items-baseline gap-2">
                                            <span className="text-3xl font-black text-neutral-900 leading-none">
                                                {dateInfo.day}
                                            </span>
                                            <span className="text-sm font-bold text-neutral-900 uppercase tracking-widest">
                                                {dateInfo.month} '
                                                {dateInfo.year.slice(2)}
                                            </span>
                                        </div>
                                        <div className="flex items-center text-xs font-bold text-neutral-900 bg-white/40 px-2 py-1 rounded">
                                            <Clock size={12} className="mr-1" />
                                            {batch.timings}
                                        </div>
                                    </div>

                                    {/* Clean Dashed Divider */}
                                    <div className="w-full border-t-2 border-dashed border-gray-200"></div>

                                    {/* Ticket Body */}
                                    <div className="p-5 flex-1 flex flex-col">
                                        <div className="mb-4">
                                            <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-widest bg-red-50 text-red-700 border border-red-100 mb-3">
                                                <MapPin
                                                    size={10}
                                                    className="mr-1"
                                                />
                                                {batch.mode}
                                            </span>
                                            <h3 className="text-lg font-black text-neutral-900 leading-tight line-clamp-2">
                                                {batch.course_name}
                                            </h3>
                                        </div>

                                        <div className="mt-auto pt-4 border-t border-gray-50">
                                            <button
                                                onClick={() => openPopup(batch)}
                                                className="w-full cursor-pointer flex items-center justify-between px-4 py-2.5 bg-neutral-900 text-white hover:bg-red-700 rounded-lg font-bold text-sm transition-colors duration-300 group/btn"
                                            >
                                                <span>
                                                    {batch.cta ||
                                                        "View Details"}
                                                </span>
                                                <ArrowRight
                                                    size={16}
                                                    className="group-hover/btn:translate-x-1 transition-transform"
                                                />
                                            </button>
                                        </div>
                                    </div>
                                </motion.div>
                            );
                        })}
                    </motion.div>
                ) : (
                    <div className="text-center py-12 text-gray-500 font-medium bg-white rounded-xl border border-gray-200 shadow-sm w-full">
                        <CalendarDays
                            size={32}
                            className="mx-auto text-gray-300 mb-3"
                        />
                        <p className="text-base text-neutral-900 font-bold">
                            No schedules available.
                        </p>
                        <p className="text-sm mt-1">
                            Please check back later for new batch announcements.
                        </p>
                    </div>
                )}
            </div>

            {/* Professional Itinerary Modal */}
            <AnimatePresence>
                {selectedBatch && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-neutral-900/60 backdrop-blur-sm"
                        onClick={closePopup}
                    >
                        <motion.div
                            initial={{ scale: 0.98, opacity: 0, y: 10 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.98, opacity: 0, y: 10 }}
                            transition={{ duration: 0.2 }}
                            className="bg-white rounded-xl w-full max-w-lg shadow-2xl relative flex flex-col max-h-[90vh] border border-gray-200"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="bg-neutral-900 px-6 py-5 flex justify-between items-center sticky top-0 z-10">
                                <h2 className="text-lg font-bold text-white pr-8 truncate">
                                    {selectedBatch.course_name}
                                </h2>
                                <button
                                    className="text-gray-400 cursor-pointer hover:text-white transition-colors p-1"
                                    onClick={closePopup}
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 overflow-y-auto custom-scrollbar">
                                {/* Structured Data Table */}
                                <div className="grid grid-cols-2 gap-px bg-gray-200 border border-gray-200 rounded-lg overflow-hidden mb-6">
                                    <div className="bg-gray-50 p-3 flex flex-col">
                                        <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-1">
                                            Start Date
                                        </span>
                                        <span className="text-sm font-bold text-neutral-900 flex items-center gap-1.5">
                                            <CalendarDays
                                                size={14}
                                                className="text-yellow-600"
                                            />
                                            {
                                                formatTicketDate(
                                                    selectedBatch.start_date,
                                                ).day
                                            }{" "}
                                            {
                                                formatTicketDate(
                                                    selectedBatch.start_date,
                                                ).month
                                            }{" "}
                                            {
                                                formatTicketDate(
                                                    selectedBatch.start_date,
                                                ).year
                                            }
                                        </span>
                                    </div>
                                    <div className="bg-gray-50 p-3 flex flex-col">
                                        <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-1">
                                            Timing
                                        </span>
                                        <span className="text-sm font-bold text-neutral-900 flex items-center gap-1.5">
                                            <Clock
                                                size={14}
                                                className="text-yellow-600"
                                            />
                                            {selectedBatch.timings}
                                        </span>
                                    </div>
                                    <div className="bg-gray-50 p-3 flex flex-col col-span-2">
                                        <span className="text-[10px] text-gray-500 font-bold uppercase tracking-widest mb-1">
                                            Instruction Mode
                                        </span>
                                        <span className="text-sm font-bold text-red-700 flex items-center gap-1.5">
                                            <MapPin size={14} />
                                            {selectedBatch.mode}
                                        </span>
                                    </div>
                                </div>

                                {/* Overview Content */}
                                <div className="prose prose-sm max-w-none">
                                    <h4 className="text-neutral-900 font-bold mb-2 text-sm uppercase tracking-widest border-b border-gray-100 pb-2">
                                        Batch Overview
                                    </h4>
                                    <div
                                        className="text-gray-600 text-sm leading-relaxed"
                                        dangerouslySetInnerHTML={{
                                            __html: safeReplace(
                                                selectedBatch.description,
                                            ),
                                        }}
                                    />
                                </div>
                            </div>

                            {/* Modal Footer */}
                            {/* <div className="p-6 border-t border-gray-200 bg-gray-50 mt-auto">
                                <Link
                                    to={redirectUrl}
                                    onClick={() =>
                                        sessionStorage.setItem(
                                            "redirectFrom",
                                            window.location.pathname,
                                        )
                                    }
                                    className="block w-full"
                                >
                                    <button className="w-full cursor-pointer flex items-center justify-center py-3.5 bg-yellow-400 hover:bg-yellow-500 text-neutral-900 font-black uppercase tracking-widest text-xs rounded-lg shadow-sm transition-colors group">
                                        Claim Admission
                                        <ArrowRight
                                            size={16}
                                            className="ml-2 group-hover:translate-x-1 transition-transform"
                                        />
                                    </button>
                                </Link>
                            </div> */}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </section>
    );
};

export default UpcomingBatches;
